import { memo, useMemo } from 'react';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { Grid, Stack, Typography } from '@mui/material';
import { algoliaClient } from '@client/algolia';
import { Hits } from '@client/algolia/InstantSearch/Hits';
import { getAlgoliaIndexName, serializeFilters } from '@sda/utils/algolia';
import { AlgoliaIndices } from '@sda/enums/algolia';
import { getLocation } from '@sda/utils/location';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { getShowPath } from '@sda/utils/url';
import { ShowCard } from '@client/components/common/BaseCard/ShowCard';
import { getEventLocation } from '@sda/utils/string';
import { getDateTime } from '@sda/utils/date';
import type { PublicShow } from '@sda/interfaces/show';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';
import type { AlgoliaShow } from '@sda/helpers/algolia.helpers';

const defaultFilters = {
  'publicShow.isIncomplete': {
    type: 'boolean',
    value: false,
  },
  'publicShow.isCancelled': {
    type: 'boolean',
    value: false,
  },
  'publicShow.endTime._seconds': {
    type: 'futureDate',
  },
  'publicShow.hasPublicTickets': {
    type: 'boolean',
    value: true,
  },
  'publicShow.hasExternalTickets': {
    type: 'boolean',
    value: true,
    not: true,
  },
  'publicShow.announceTime._seconds': {
    type: 'pastDate',
  },
};

const attributesToRetrieve = [
  'publicShow.publicImageUrl',
  'publicShow.title',
  'publicShow.capacity',
  'publicShow.city',
  'publicShow.region',
  'publicShow.startTime',
  'publicShow.timezoneOverride',
  'publicShow.currencyOverride',
];

export const NearbyShows = memo<Pick<PublicShow & FetchDataType, 'id' | 'fuzzyLatLng'>>(({ id, fuzzyLatLng }) => {
  const isMobile = useIsMobile();

  const filters = useMemo(() => {
    const refinedFilters = {
      ...defaultFilters,
      objectID: {
        type: 'string',
        value: id,
        not: true,
      },
    };

    return serializeFilters(refinedFilters as Record<string, AlgoliaFilter>);
  }, [id]);

  const filterLocation = useMemo(() => {
    const location = getLocation(fuzzyLatLng);

    return `${location.lat}, ${location.lng}`;
  }, [fuzzyLatLng]);

  return (
    <InstantSearch
      searchClient={algoliaClient}
      indexName={getAlgoliaIndexName(AlgoliaIndices.SHOWS)}
    >
      <Configure
        hitsPerPage={3}
        filters={filters}
        aroundLatLng={filterLocation}
        aroundRadius="all"
        aroundPrecision={100000}
        attributesToRetrieve={attributesToRetrieve}
        attributesToHighlight={[]}
      />

      <Stack spacing={isMobile ? 3 : 4}>
        <Typography variant="subtitle2">
          Nearby Upcoming Shows
        </Typography>

        <div>
          <Hits<any, AlgoliaShow>
            component={Grid}
            container
            spacing={3}
            hitComponent={({ hit }) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
              >
                <ShowCard
                  showId={hit.objectID}
                  currency={hit.publicShow.currencyOverride}
                  imgSrc={hit.publicShow.publicImageUrl}
                  path={getShowPath(hit.objectID)}
                  title={hit.publicShow.title}
                  location={getEventLocation(hit.publicShow)}
                  startTime={getDateTime(hit.publicShow.startTime).setZone(hit.publicShow.timezoneOverride).toFormat('LLLL d, h:mm a')}
                />
              </Grid>
            )}
          />
        </div>
      </Stack>
    </InstantSearch>
  );
});
